import { TAILWIND_LOOKUP } from "@/lib/tailwind";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { ReactFramework } from "@/utils/templateProjects";
import React from "react";
import { useMemo } from "react";
import { useActiveProject } from "@/utils/utils";

export type TypographyPreset = {
  name: string;
  classes: string[];
  tag: string;
  preview: React.ReactNode; // Used to render the preview
  jsxString: string; // Used for code mutation
  htmlInstantUpdate: string; // Used for instant update (must be valid HTML)
};

/**
 * Config JSON takes the form:
 * {
 *  typography: [
 *    {
 *      name: "Header 1",
 *      preview: "Aa",
 *      tag: "h1",
 *      classes: ["text-4xl", "font-extrabold", "tracking-tight", "lg:text-5xl"],
 *      previewClasses: ["text-4xl", "font-extrabold", "tracking-tight", "lg:text-5xl"] // Optional - if different from classes
 *    }
 *  ]
 * }
 * @param config
 * @returns TypographyPreset[]
 */

const createOptionsFromConfig = (config: any): TypographyPreset[] => {
  if (!config.typography) {
    return [];
  }

  let options: TypographyPreset[] = [];

  config.typography.forEach((typography) => {
    if (
      !typography.name ||
      !typography.preview ||
      !typography.tag ||
      !typography.classes
    ) {
      return;
    }

    let cssStylesForInstantUpdate = "";
    let leftoverClasses = [];
    typography.classes.forEach((cssClass) => {
      const cssStyles = TAILWIND_LOOKUP[cssClass];
      if (cssStyles) {
        cssStylesForInstantUpdate += `${TAILWIND_LOOKUP[cssClass] || ""} `;
      } else {
        // @ts-expect-error TS2345
        leftoverClasses.push(cssClass);
      }
    });

    options.push({
      name: typography.name,
      preview: React.createElement(typography.tag, {
        className: (typography.previewClasses || typography.classes).join(" "),
        children: typography.preview,
      }),
      tag: typography.tag,
      classes: typography.classes,
      jsxString: `<${typography.tag} className="${typography.classes.join(
        " "
      )}">${typography.name}</${typography.tag}>`,
      htmlInstantUpdate: `<${
        typography.tag
      } style="${cssStylesForInstantUpdate}" class="${leftoverClasses.join(
        " "
      )}">${typography.name}</${typography.tag}>`,
    });
  });

  return options;
};

const defaultOption: TypographyPreset = {
  name: "Default",
  classes: [],
  tag: "div",
  preview: <div>Text</div>,
  jsxString: `<div>Text</div>`,
  htmlInstantUpdate: `<div>Text</div>`,
};

const defaultOptionExpo: TypographyPreset = {
  name: "Default",
  classes: [],
  tag: "View",
  preview: <div>Text</div>,
  jsxString: `<View>Text</View>`,
  htmlInstantUpdate: `<div>Text</div>`,
};

const useTypographyPresets = () => {
  const conditions = useCodebaseStore((state) => state.conditions);

  const project = useActiveProject();

  const options = useMemo(() => {
    if (!conditions) {
      return [];
    }

    if (!conditions["config"]) {
      return [];
    }

    // Means the config wasn't parsed successfully
    if (!conditions["config"].met) {
      return [];
    }

    const { data: config } = conditions["config"];

    return createOptionsFromConfig(config);
  }, [conditions]);

  return {
    options,
    defaultOption:
      project?.framework === ReactFramework.EXPO
        ? defaultOptionExpo
        : defaultOption,
  };
};

export default useTypographyPresets;
